import { useQuery } from '@tanstack/react-query';
import { http, returnIfNotFoundError } from 'services/http';
import { COMMENTS_GET_MANY } from 'api/query-keys.ts';
import type { TCommentRes } from 'api/swagger';
import { PAGINATION_DEFAULT_LIMIT } from 'shared/constants/paginations';
import type { WithPagination } from 'shared/utils/pagination.ts';
import { preparePagination } from 'shared/utils/pagination.ts';
import type { IWithPagination } from 'types/api-query';
import type { ECommentEntityName } from 'types/comment.ts';

interface IParams extends IWithPagination {
  entityName: ECommentEntityName;
  entityId: number;
}

export const useGetComments = (params?: IParams) => {
  const { limit = PAGINATION_DEFAULT_LIMIT, page = 1, entityName, entityId } = params || {};

  const qs = new URLSearchParams({
    limit: String(limit),
    page: String(page),
    'filter[entityName]': String(entityName),
    'filter[entityId]': String(entityId),
  });

  return useQuery<WithPagination<TCommentRes[]>>({
    queryKey: [COMMENTS_GET_MANY, { entityName, entityId, page, limit }],
    queryFn: async () => {
      try {
        const { data, headers } = await http.get<TCommentRes[]>(`/v1/comments?${qs.toString()}`);
        const pagination = preparePagination(headers);

        return { data, pagination };
      } catch (error) {
        return returnIfNotFoundError<WithPagination<[]>>(error, { data: [], pagination: null });
      }
    },
    retry: false,
    refetchOnWindowFocus: false,
    keepPreviousData: false,
    cacheTime: 10 * 1000,
  });
};
